import * as lo from "lodash";
import { Modal } from "bootstrap";

export class TriggeredAction {
  static None = new TriggeredAction(
    "None",
    "There are no assets to be handled.",
  );
  static Cancel = new TriggeredAction(
    "Cancel",
    "Handover process was canceled.",
  );
  static ToEmployee = new TriggeredAction(
    "ToEmployee",
    "Assets were assigned to an employee.",
  );
  static ToStore = new TriggeredAction(
    "ToStore",
    "Assets were assigned to a store.",
  );

  constructor(name, description) {
    this.name = name;
    this.description = description;
  }
  toString() {
    return `TriggeredAction.${this.name}`;
  }
}

class GoingActiveEvent {}

export class AwayHandler {
  /**
   * Handles warnings for navigating away from a handover process
   *
   * Required interface:
   * @param actionToTrigger - function that returns a TriggeredAction.
   *    if navigation is done in the browser, a "turbo:before-visit" event will be passed
   *    if navigation is done by term_ctrl, nothing will be passed
   * @param controllerCtxt - the terminal_controller - needs to reference a goingAwayModalTarget (checked in the constructor)
   * @param sound - a Sound instance. Needed Sounds are checked in the constructor.
   */
  constructor({ actionToTrigger, controllerCtxt, sound }) {
    this.actionToTrigger = actionToTrigger;
    this.ctrl = controllerCtxt;
    this.sound = this.getSound(sound);
    this.goingAwayModal = this.getGoingAwayModal();
  }

  getSound(sound) {
    const neededSounds = ["cancel", "error", "toEmployee", "toStore"];
    const registeredSounds = Object.keys(sound.sounds);
    const missingSounds = lo.difference(neededSounds, registeredSounds);
    if (missingSounds.length > 0) {
      throw new Error(
        `Failed to setup AwayHandler.\n\tMissing Sounds: ${missingSounds}`,
      );
    }
    return sound;
  }

  getGoingAwayModal() {
    if (!this.ctrl.hasGoingAwayModalTarget) {
      throw new Error(
        "Failed to setup AwayHandler. Could not find goingAwayModalTarget",
      );
    }
    return Modal.getOrCreateInstance(this.ctrl.goingAwayModalTarget);
  }

  registerNavigateAwayWarn() {
    this.cleanupController = new AbortController();
    const signal = this.cleanupController.signal;
    document.addEventListener(
      "turbo:before-visit",
      async (event) => {
        switch (this.actionToTrigger(event)) {
          case TriggeredAction.None:
            return;
          case TriggeredAction.ToEmployee:
            await this.sound.playOnce("toEmployee");
            await this.ctrl.flagAssetsDirty({ includeAssigned: false });
            return;
          case TriggeredAction.ToStore:
            await this.sound.playOnce("toStore");
            await this.ctrl.flagAssetsDirty({ includeAssigned: false });
            return;
          case TriggeredAction.Cancel:
          default:
            await this.ctrl.flagAssetsDirty({ includeAssigned: true });
            await this.sound.playOnce("cancel");
        }
      },
      {
        signal,
      },
    );
  }

  unregisterNavigateAwayWarn() {
    this.cleanupController.abort("unregistered");
  }

  async handleGoingInactive() {
    switch (this.actionToTrigger()) {
      case TriggeredAction.None:
        this.ctrl.handleGoingInactive();
        break;
      default: {
        const userDidInteract =
          await this.showWarningAndWaitForUserInteraction();
        if (!userDidInteract) {
          this.ctrl.handleGoingInactive();
        }
      }
    }
  }

  async showWarningAndWaitForUserInteraction() {
    try {
      await this.warnForgottenScan();
    } catch (warnRejectedReason) {
      return warnRejectedReason instanceof GoingActiveEvent;
    } finally {
      await this.stopWarnForgottenScan();
    }
    return false;
  }

  async warnForgottenScan() {
    this.goingAwayModal.show();
    this.sound.startPlay("error");
    return new Promise((resolve, reject) => {
      const resolveTimeoutID = setTimeout(() => {
        resolve();
        this.cancelWarningData = undefined;
      }, 10_000);
      this.cancelWarningData = {
        reject,
        resolveTimeoutID,
      };
    });
  }

  async stopWarnForgottenScan() {
    this.goingAwayModal.hide();
    this.sound.stopPlay("error");
  }

  cancelWarning(reason = new GoingActiveEvent()) {
    if (this.cancelWarningData) {
      clearTimeout(this.cancelWarningData.resolveTimeoutID);
      this.cancelWarningData.reject(reason);
      this.cancelWarningData = undefined;
    }
  }
  async handleGoingActive() {
    this.cancelWarning();
    this.ctrl.handleGoingActive();
  }
}

export const REQUIRED_MP3_VALUES = [
  "cancelMp3Value",
  "errorMp3Value",
  "toStoreMp3Value",
  "toEmployeeMp3Value",
];
